// Research Mode Panel Component
import { useState, useEffect } from "react";
import axios from "axios";
import {
  FileText,
  Download,
  RefreshCw,
  Users,
  CheckCircle,
  AlertCircle,
  ShieldCheck,
  DollarSign,
  Globe,
  Layers,
  TrendingUp,
  Scale,
  BookOpen
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Cell
} from "recharts";
import { Badge } from "../common";
import { API } from "../../lib/constants";
import { ShareCitePanel } from "../share/ShareCitePanel";
import { DiscoveryAnalyses } from "./DiscoveryAnalyses";

export const ResearchModePanel = ({ researchStats, onLoad, showNotification }) => {
  const [loading, setLoading] = useState(false);
  const [biasAudit, setBiasAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    if (!researchStats) {
      setLoading(true);
      onLoad().finally(() => setLoading(false));
    }
  }, []);

  const runBiasAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/bias-audit`);
      setBiasAudit(res.data);
      showNotification("Bias audit completed", "success");
    } catch (error) {
      showNotification("Bias audit failed", "error");
    } finally {
      setAuditLoading(false);
    }
  };

  const downloadExport = async () => {
    try {
      const response = await axios.get(`${API}/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `aioo-export-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
      showNotification("Data exported successfully!", "success");
    } catch (error) {
      showNotification("Export failed", "error");
    }
  };

  if (loading) {
    return (
      <div className="space-y-5 animate-fade-in py-8">
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-5 w-40 bg-white/[0.04] animate-pulse rounded" />
              <div className="h-3 w-60 bg-white/[0.04] animate-pulse rounded" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[1, 2].map(i => (
            <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6 space-y-3">
              <div className="h-4 w-36 bg-white/[0.04] animate-pulse rounded" />
              <div className="h-12 w-24 bg-white/[0.04] animate-pulse rounded" />
              <div className="h-3 w-48 bg-white/[0.04] animate-pulse rounded" />
              <div className="space-y-2 mt-3">
                {[1, 2, 3].map(j => (
                  <div key={j} className="flex justify-between">
                    <div className="h-3 w-32 bg-white/[0.04] animate-pulse rounded" />
                    <div className="h-3 w-16 bg-white/[0.04] animate-pulse rounded" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-[10px] text-slate-500 animate-pulse">Loading research statistics...</p>
      </div>
    );
  }

  if (!researchStats) {
    return (
      <div className="text-center py-20">
        <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
        <p>Unable to load research statistics</p>
        <button onClick={onLoad} className="mt-4 px-4 py-2 bg-primary text-white rounded-lg">Retry</button>
      </div>
    );
  }

  const kf = researchStats.key_findings;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30">
              <FileText className="w-7 h-7 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Research Mode</h2>
              <p className="text-sm text-muted-foreground">Academic statistics with confidence intervals</p>
            </div>
          </div>
        </div>

        {/* Sample Warning */}
        {researchStats.metadata?.sample_warning && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-400">{researchStats.metadata.sample_warning}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Confidence interval: {researchStats.metadata.confidence_level} using {researchStats.metadata.method}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Key Findings with CIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Female Founders Finding */}
        <div className="bg-card border border-pink-500/30 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-lg bg-pink-500/20">
              <Users className="w-5 h-5 text-pink-400" />
            </div>
            <h3 className="font-semibold">Female Founders → Care</h3>
          </div>
          <div className="text-5xl font-black text-pink-400 mb-2">
            {kf?.female_founders_care_oriented?.point_estimate}
          </div>
          <p className="text-sm text-muted-foreground mb-3">{kf?.female_founders_care_oriented?.description}</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">95% Confidence Interval:</span>
              <span className="font-mono text-pink-400">{kf?.female_founders_care_oriented?.confidence_interval}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Margin of Error:</span>
              <span className="font-mono">{kf?.female_founders_care_oriented?.margin_of_error}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Sample Size:</span>
              <span className="font-mono">n = {kf?.female_founders_care_oriented?.n}</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4 italic">
            {kf?.female_founders_care_oriented?.interpretation}
          </p>
        </div>

        {/* Male Founders Comparison */}
        <div className="bg-card border border-blue-500/30 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <h3 className="font-semibold">Male Founders → Care (Comparison)</h3>
          </div>
          <div className="text-5xl font-black text-blue-400 mb-2">
            {kf?.male_founders_care_oriented?.point_estimate}
          </div>
          <p className="text-sm text-muted-foreground mb-3">{kf?.male_founders_care_oriented?.description}</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">95% Confidence Interval:</span>
              <span className="font-mono text-blue-400">{kf?.male_founders_care_oriented?.confidence_interval}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Margin of Error:</span>
              <span className="font-mono">{kf?.male_founders_care_oriented?.margin_of_error}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Sample Size:</span>
              <span className="font-mono">n = {kf?.male_founders_care_oriented?.n}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Difference Highlight */}
      <div className="bg-gradient-to-r from-pink-500/10 via-violet-500/10 to-blue-500/10 border border-violet-500/30 rounded-xl p-6 text-center">
        <p className="text-sm text-muted-foreground mb-2">Observed Difference in Care Orientation</p>
        <div className="text-6xl font-black bg-gradient-to-r from-pink-400 to-blue-400 bg-clip-text text-transparent">
          +{kf?.difference?.point_estimate_diff}
        </div>
        <p className="text-sm text-muted-foreground mt-2">{kf?.difference?.note}</p>
      </div>

      {/* Mixed & LGBTQIA+ Founders */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Mixed Gender Founders */}
        {kf?.mixed_founders_care_oriented && (
          <div className="bg-card border border-emerald-500/30 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <Users className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="font-semibold">Mixed-Gender Teams → Care</h3>
            </div>
            <div className="text-4xl font-black text-emerald-400 mb-2">
              {kf.mixed_founders_care_oriented.point_estimate}
            </div>
            <p className="text-sm text-muted-foreground mb-3">{kf.mixed_founders_care_oriented.description}</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">95% CI:</span>
                <span className="font-mono text-emerald-400">{kf.mixed_founders_care_oriented.confidence_interval}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sample Size:</span>
                <span className="font-mono">n = {kf.mixed_founders_care_oriented.n}</span>
              </div>
            </div>
          </div>
        )}

        {/* LGBTQIA+ Founders */}
        {kf?.lgbtq_founders_care_oriented && kf.lgbtq_founders_care_oriented.n > 0 && (
          <div className="bg-card border border-violet-500/30 rounded-xl p-6" data-testid="research-lgbtq-card">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-violet-500/20">
                <Users className="w-5 h-5 text-violet-400" />
              </div>
              <h3 className="font-semibold">LGBTQIA+ Founders → Care</h3>
            </div>
            <div className="text-4xl font-black text-violet-400 mb-2">
              {kf.lgbtq_founders_care_oriented.point_estimate}
            </div>
            <p className="text-sm text-muted-foreground mb-3">{kf.lgbtq_founders_care_oriented.description}</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">95% CI:</span>
                <span className="font-mono text-violet-400">{kf.lgbtq_founders_care_oriented.confidence_interval}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sample Size:</span>
                <span className="font-mono">n = {kf.lgbtq_founders_care_oriented.n}</span>
              </div>
            </div>
            {kf.lgbtq_founders_care_oriented.caveat && (
              <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-xs text-amber-400">{kf.lgbtq_founders_care_oriented.caveat}</p>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-3 italic">
              {kf.lgbtq_founders_care_oriented.interpretation}
            </p>
          </div>
        )}
      </div>

      {/* Data Representativeness */}
      {researchStats.data_representativeness && (
        <div className="bg-card border border-amber-500/30 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <AlertCircle className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Data Representativeness</h3>
              <p className="text-sm text-muted-foreground">Known gaps and limitations in the dataset</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-black/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-400">{researchStats.data_representativeness.unknown_founders_pct}%</p>
              <p className="text-xs text-muted-foreground">Unknown Founders</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold">{researchStats.data_representativeness.total_sources}</p>
              <p className="text-xs text-muted-foreground">Data Sources</p>
            </div>
            {Object.entries(researchStats.data_representativeness.founder_composition_breakdown || {}).filter(([k]) => k === 'female_only').map(([k, v]) => (
              <div key={k} className="bg-black/20 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-pink-400">{v}</p>
                <p className="text-xs text-muted-foreground">Female-Founded</p>
              </div>
            ))}
            <div className="bg-black/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-violet-400">{kf?.lgbtq_founders_care_oriented?.n || 0}</p>
              <p className="text-xs text-muted-foreground">LGBTQIA+ Declared</p>
            </div>
          </div>
          
          <div className="p-4 bg-amber-500/10 rounded-lg">
            <p className="text-sm text-amber-300">{researchStats.data_representativeness.insight}</p>
          </div>
        </div>
      )}

      {/* Statistical Significance Test */}
      {kf?.statistical_test && (
        <div className={`rounded-xl p-6 border ${kf.statistical_test.significant_at_05 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-lg ${kf.statistical_test.significant_at_05 ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
              <CheckCircle className={`w-6 h-6 ${kf.statistical_test.significant_at_05 ? 'text-emerald-400' : 'text-amber-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Statistical Significance Test</h3>
              <p className="text-sm text-muted-foreground">Two-proportion z-test</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="text-center p-3 bg-black/20 rounded-lg">
              <p className="text-2xl font-bold font-mono">{kf.statistical_test.z_statistic}</p>
              <p className="text-xs text-muted-foreground">z-statistic</p>
            </div>
            <div className="text-center p-3 bg-black/20 rounded-lg">
              <p className={`text-2xl font-bold font-mono ${kf.statistical_test.p_value <= 0.001 ? 'text-emerald-400' : ''}`}>
                {kf.statistical_test.p_value <= 0.001 ? '<0.001' : kf.statistical_test.p_value}
              </p>
              <p className="text-xs text-muted-foreground">p-value</p>
            </div>
            <div className="text-center p-3 bg-black/20 rounded-lg">
              <p className="text-2xl font-bold">{kf.statistical_test.significant_at_05 ? '✓' : '✗'}</p>
              <p className="text-xs text-muted-foreground">Sig. at α=0.05</p>
            </div>
            <div className="text-center p-3 bg-black/20 rounded-lg">
              <p className="text-2xl font-bold">{kf.statistical_test.significant_at_01 ? '✓' : '✗'}</p>
              <p className="text-xs text-muted-foreground">Sig. at α=0.01</p>
            </div>
          </div>
          
          <div className={`p-4 rounded-lg ${kf.statistical_test.significant_at_05 ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
            <p className={`font-medium ${kf.statistical_test.significant_at_05 ? 'text-emerald-300' : 'text-amber-300'}`}>
              {kf.statistical_test.interpretation}
            </p>
          </div>
        </div>
      )}

      {/* === EXPANDED RESEARCH ANALYSES === */}

      {/* 1. Monetization × Value Orientation */}
      <MonetizationOrientationSection data={researchStats.monetization_orientation} />

      {/* 2. Founder Gender × Monetization */}
      <FounderMonetizationSection data={researchStats.founder_monetization} />

      {/* 3. Regional Value Orientation */}
      <RegionalOrientationSection data={researchStats.regional_orientation} />

      {/* 4. Founder Diversity by Region */}
      <RegionalDiversitySection data={researchStats.regional_diversity} />

      {/* 5. Gender Gap by Sector */}
      <SectorGenderSection data={researchStats.sector_gender_gap} />

      {/* 6. Governance Framing × Founders */}
      <GovernanceDiversitySection data={researchStats.governance_diversity} />

      {/* 7. Temporal Trends */}
      <TemporalTrendsSection
        orientationData={researchStats.temporal_trends}
        founderData={researchStats.founder_trends}
      />

      {/* 8-11. Discovery Analyses (new deep-dive sections) */}
      <DiscoveryAnalyses />

      {/* Methodology */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-muted-foreground" />
          Methodology
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <h4 className="font-medium mb-2">Data Collection</h4>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Source: {researchStats.methodology?.data_source}</li>
              <li>• Period: {researchStats.methodology?.time_period}</li>
              <li>• Method: {researchStats.methodology?.coding_method}</li>
              <li>• Gender coding: {researchStats.methodology?.gender_coding}</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Limitations</h4>
            <ul className="space-y-1 text-muted-foreground">
              {researchStats.methodology?.limitations?.map((l, i) => (
                <li key={`lim-${i}-${l.slice(0,20)}`}>• {l}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bias Audit Section */}
      <div className="bg-card border border-violet-500/30 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-violet-500/20">
              <ShieldCheck className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Bias Audit</h3>
              <p className="text-sm text-muted-foreground">Model-level bias mitigation verification</p>
            </div>
          </div>
          <button
            onClick={runBiasAudit}
            disabled={auditLoading}
            data-testid="run-bias-audit-btn"
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50"
          >
            {auditLoading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" />
                Run Audit
              </>
            )}
          </button>
        </div>

        {biasAudit ? (
          <BiasAuditResults biasAudit={biasAudit} />
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <ShieldCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Click "Run Audit" to verify classification model is bias-free</p>
            <p className="text-sm mt-2">Analyzes correlation between demographics and classifications</p>
          </div>
        )}
      </div>

      {/* Definitions */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-lg mb-4">Category Definitions (Codebook)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {Object.entries(researchStats.methodology?.orientation_definitions || {}).map(([key, def]) => (
            <div key={key} className="p-3 bg-muted/50 rounded-lg">
              <Badge type={key} variant="orientation" showTooltip={false} />
              <p className="text-muted-foreground mt-2">{def}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Share & Cite */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-emerald-400" />
          Cite This Dataset
        </h3>
        <ShareCitePanel inline />
      </div>
    </div>
  );
};

// Shared chart styling
const TT = { contentStyle: { backgroundColor: 'hsl(240, 6%, 7%)', border: '1px solid hsl(240, 5%, 17%)', borderRadius: '8px' }, isAnimationActive: false, wrapperStyle: { pointerEvents: 'none' } };
const AX = "hsl(240, 5%, 65%)";
const GR = "hsl(240, 5%, 17%)";
const TK = { fontSize: 11 };

const ORIENT_COLORS = { care: "#10b981", utility: "#3b82f6", creative: "#8b5cf6", extraction: "#ef4444" };
const ORIENT_LABELS = { care: "Care", utility: "Utility", creative: "Creative", extraction: "Extraction" };
const FOUNDER_COLORS = { female_only: "#ec4899", male_only: "#3b82f6", mixed: "#8b5cf6", unknown: "#6b7280" };

const SectionCard = ({ icon: Icon, title, subtitle, color = "text-primary", children }) => (
  <div className="bg-card border border-border rounded-xl p-6" data-testid={`research-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-2 mb-5">
      <div className={`p-2 rounded-lg bg-${color.split('-')[1] || 'primary'}-500/20`} style={{ background: `${color === 'text-primary' ? 'hsl(217,91%,60%,0.15)' : ''}` }}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div>
        <h3 className="font-semibold text-base">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

// 1. Monetization × Value Orientation
const MonetizationOrientationSection = ({ data }) => {
  if (!data) return null;
  const orientations = ["care", "utility", "creative", "extraction"];
  const allModels = new Set();
  orientations.forEach(o => { if (data[o]) Object.keys(data[o].models).forEach(m => allModels.add(m)); });

  const chartData = [...allModels].map(m => {
    const row = { model: m.replace(/_/g, ' ') };
    orientations.forEach(o => { row[o] = data[o]?.models?.[m]?.pct || 0; });
    return row;
  }).sort((a, b) => b.care - a.care);

  return (
    <SectionCard icon={DollarSign} title="Monetization × Value Orientation" subtitle="How do business models differ across value orientations?" color="text-amber-400">
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke={GR} />
            <XAxis type="number" stroke={AX} tick={TK} unit="%" />
            <YAxis dataKey="model" type="category" stroke={AX} width={90} tick={TK} />
            <Tooltip {...TT} formatter={(v) => `${v}%`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {orientations.map(o => (
              <Bar key={o} dataKey={o} fill={ORIENT_COLORS[o]} name={ORIENT_LABELS[o]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        Care-centered apps are most likely to use enterprise ({data.care?.models?.enterprise?.pct || 0}%) and free ({data.care?.models?.free?.pct || 0}%) models — suggesting mission-driven funding over consumer monetization.
      </p>
    </SectionCard>
  );
};

// 2. Founder Gender × Monetization
const FounderMonetizationSection = ({ data }) => {
  if (!data) return null;
  const founders = ["female_only", "male_only", "mixed"];
  const founderLabels = { female_only: "Female", male_only: "Male", mixed: "Mixed" };
  const allModels = new Set();
  founders.forEach(f => { if (data[f]) Object.keys(data[f].models).forEach(m => allModels.add(m)); });

  const chartData = [...allModels].map(m => {
    const row = { model: m.replace(/_/g, ' ') };
    founders.forEach(f => { row[f] = data[f]?.models?.[m]?.pct || 0; });
    return row;
  }).sort((a, b) => (b.female_only || 0) - (a.female_only || 0));

  return (
    <SectionCard icon={Users} title="Founder Gender × Monetization" subtitle="Do founding team demographics influence business model choices?" color="text-pink-400">
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke={GR} />
            <XAxis type="number" stroke={AX} tick={TK} unit="%" />
            <YAxis dataKey="model" type="category" stroke={AX} width={90} tick={TK} />
            <Tooltip {...TT} formatter={(v) => `${v}%`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {founders.map(f => (
              <Bar key={f} dataKey={f} fill={FOUNDER_COLORS[f]} name={founderLabels[f]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </SectionCard>
  );
};

// 3. Regional Value Orientation
const RegionalOrientationSection = ({ data }) => {
  if (!data || data.length === 0) return null;
  const chartData = data.filter(r => r.total >= 10).map(r => ({
    region: r.region,
    care: r.orientations?.care?.pct || 0,
    utility: r.orientations?.utility?.pct || 0,
    creative: r.orientations?.creative?.pct || 0,
    extraction: r.orientations?.extraction?.pct || 0,
    total: r.total,
  }));

  return (
    <SectionCard icon={Globe} title="Regional Value Orientation" subtitle="How does AI's purpose vary across the world?" color="text-cyan-400">
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={GR} />
            <XAxis dataKey="region" stroke={AX} tick={TK} angle={-20} textAnchor="end" height={60} interval={0} />
            <YAxis stroke={AX} tick={TK} unit="%" />
            <Tooltip {...TT} formatter={(v) => `${v}%`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="care" fill={ORIENT_COLORS.care} name="Care" stackId="a" />
            <Bar dataKey="utility" fill={ORIENT_COLORS.utility} name="Utility" stackId="a" />
            <Bar dataKey="creative" fill={ORIENT_COLORS.creative} name="Creative" stackId="a" />
            <Bar dataKey="extraction" fill={ORIENT_COLORS.extraction} name="Extraction" stackId="a" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {data.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {data.filter(r => r.total >= 10).sort((a, b) => b.care_pct - a.care_pct).slice(0, 4).map((r, i) => (
            <div key={r.region || `region-${i}`} className="bg-muted/30 rounded-lg p-3">
              <div className="text-lg font-bold text-emerald-400">{r.care_pct}%</div>
              <div className="text-xs font-medium">{r.region}</div>
              <div className="text-xs text-muted-foreground">care-oriented (n={r.total})</div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

// 4. Regional Diversity
const RegionalDiversitySection = ({ data }) => {
  if (!data || data.length === 0) return null;
  const chartData = data.filter(r => r.total >= 10).map(r => ({
    region: r.region,
    diversity: r.diversity_score,
    female: r.founders?.female_only?.pct || 0,
    mixed: r.founders?.mixed?.pct || 0,
    total: r.total,
  }));

  return (
    <SectionCard icon={Users} title="Founder Diversity by Region" subtitle="Which regions have the most gender-diverse founding teams?" color="text-violet-400">
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={GR} />
            <XAxis dataKey="region" stroke={AX} tick={TK} angle={-20} textAnchor="end" height={60} interval={0} />
            <YAxis stroke={AX} tick={TK} unit="%" />
            <Tooltip {...TT} formatter={(v) => `${v}%`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="female" fill="#ec4899" name="Female-Only" stackId="a" />
            <Bar dataKey="mixed" fill="#8b5cf6" name="Mixed Teams" stackId="a" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </SectionCard>
  );
};

// 5. Sector Gender Gap
const SectorGenderSection = ({ data }) => {
  if (!data || data.length === 0) return null;
  const sectorColors = { Healthcare: "#10b981", Finance: "#f59e0b", Education: "#3b82f6", Creative: "#ec4899", Productivity: "#8b5cf6" };
  const chartData = data.map(s => ({
    sector: s.sector,
    female_pct: s.female_pct,
    total: s.total,
    fill: sectorColors[s.sector] || "#6b7280",
  }));

  return (
    <SectionCard icon={Layers} title="Gender Gap by Sector" subtitle="Where is female founding concentrated vs. absent?" color="text-emerald-400">
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={GR} />
            <XAxis dataKey="sector" stroke={AX} tick={TK} interval={0} />
            <YAxis stroke={AX} tick={TK} unit="%" />
            <Tooltip {...TT} formatter={(v, n, p) => [`${v}% (n=${p.payload.total})`, 'Female-founded']} />
            <Bar dataKey="female_pct" name="Female-Founded %" radius={[4, 4, 0, 0]}>
              {chartData.map((e) => <Cell key={e.name || e.region} fill={e.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        Healthcare leads in female founding ({data[0]?.female_pct}%), while Creative and Productivity sectors show the widest gender gaps.
      </p>
    </SectionCard>
  );
};

// 6. Governance × Diversity
const GovernanceDiversitySection = ({ data }) => {
  if (!data || data.length === 0) return null;
  const govLabels = { system_aware: "System-Aware (Ethical)", neutral: "Neutral", market_first: "Market-First" };
  const govColors = { system_aware: "#10b981", neutral: "#6b7280", market_first: "#f59e0b" };
  const chartData = data.map(g => ({
    governance: govLabels[g.governance] || g.governance,
    diversity: g.diversity_score,
    female: g.founders?.female_only?.pct || 0,
    mixed: g.founders?.mixed?.pct || 0,
    total: g.total,
    fill: govColors[g.governance] || "#6b7280",
  }));

  return (
    <SectionCard icon={Scale} title="Governance Framing × Team Diversity" subtitle="Are ethically-framed apps more likely to have diverse teams?" color="text-teal-400">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {chartData.map((g, i) => (
          <div key={g.governance || `gov-${i}`} className="bg-muted/30 border border-border rounded-lg p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{g.governance}</div>
            <div className="text-2xl font-bold" style={{ color: g.fill }}>{g.diversity}%</div>
            <div className="text-xs text-muted-foreground">diverse teams (n={g.total})</div>
            <div className="mt-3 space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-pink-400">Female-only</span>
                <span>{g.female}%</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-violet-400">Mixed</span>
                <span>{g.mixed}%</span>
              </div>
            </div>
            <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${g.diversity}%`, backgroundColor: g.fill }} />
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

// 7. Temporal Trends
const TemporalTrendsSection = ({ orientationData, founderData }) => {
  if ((!orientationData || orientationData.length === 0) && (!founderData || founderData.length === 0)) return null;

  const careTrend = (orientationData || []).filter(t => t.total >= 5);
  const founderTrend = (founderData || []).filter(t => t.total >= 5);

  return (
    <SectionCard icon={TrendingUp} title="Temporal Shifts" subtitle="How is the AI ecosystem evolving over time?" color="text-blue-400">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Care orientation over time */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Care Orientation Over Time</h4>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={careTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke={GR} />
                <XAxis dataKey="year" stroke={AX} tick={TK} />
                <YAxis stroke={AX} tick={TK} unit="%" />
                <Tooltip {...TT} formatter={(v, n, p) => [`${v}% (n=${p.payload.total})`, 'Care %']} />
                <Line type="monotone" dataKey="care_pct" stroke="#10b981" strokeWidth={2} dot={{ r: 4, fill: '#10b981' }} name="Care %" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Founder diversity over time */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Founder Diversity Over Time</h4>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={founderTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke={GR} />
                <XAxis dataKey="year" stroke={AX} tick={TK} />
                <YAxis stroke={AX} tick={TK} unit="%" />
                <Tooltip {...TT} formatter={(v, n, p) => [`${v}% (n=${p.payload.total})`, n]} />
                <Line type="monotone" dataKey="female_pct" stroke="#ec4899" strokeWidth={2} dot={{ r: 4, fill: '#ec4899' }} name="Female %" />
                <Line type="monotone" dataKey="diverse_pct" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4, fill: '#8b5cf6' }} name="Diverse %" />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

// Bias Audit Results Sub-component
const BiasAuditResults = ({ biasAudit }) => (
  <div className="space-y-4">
    {/* Audit Summary */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-muted/30 rounded-lg p-3">
        <p className="text-xs text-muted-foreground">Apps Analyzed</p>
        <p className="text-xl font-bold">{biasAudit.audit_summary?.total_apps_analyzed}</p>
      </div>
      <div className="bg-muted/30 rounded-lg p-3">
        <p className="text-xs text-muted-foreground">Blind Codings</p>
        <p className="text-xl font-bold">{biasAudit.audit_summary?.total_blind_codings}</p>
      </div>
      <div className="bg-muted/30 rounded-lg p-3">
        <p className="text-xs text-muted-foreground">Multi-Coder Apps</p>
        <p className="text-xl font-bold">{biasAudit.audit_summary?.apps_with_multiple_coders}</p>
      </div>
      <div className="bg-muted/30 rounded-lg p-3">
        <p className="text-xs text-muted-foreground">Agreement Rate</p>
        <p className="text-xl font-bold">
          {biasAudit.blind_vs_original_agreement?.agreement_rate !== null 
            ? `${biasAudit.blind_vs_original_agreement.agreement_rate}%` 
            : 'N/A'}
        </p>
      </div>
    </div>

    {/* Cohen's Kappa */}
    {biasAudit.inter_rater_reliability?.cohens_kappa && (
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
        <h4 className="font-semibold mb-2 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-emerald-400" />
          Inter-Rater Reliability (Cohen's Kappa)
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Kappa</p>
            <p className="font-mono text-lg font-bold text-emerald-400">
              {biasAudit.inter_rater_reliability.cohens_kappa.kappa}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Observed Agreement</p>
            <p className="font-mono">{biasAudit.inter_rater_reliability.cohens_kappa.observed_agreement}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Expected Agreement</p>
            <p className="font-mono">{biasAudit.inter_rater_reliability.cohens_kappa.expected_agreement}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Interpretation</p>
            <p className="font-medium">{biasAudit.inter_rater_reliability.cohens_kappa.interpretation}</p>
          </div>
        </div>
      </div>
    )}

    {/* Bias Analysis */}
    <div className={`rounded-lg p-4 ${
      biasAudit.bias_analysis?.bias_conclusion?.includes('VALIDATED') 
        ? 'bg-emerald-500/10 border border-emerald-500/30'
        : biasAudit.bias_analysis?.bias_conclusion?.includes('INCONCLUSIVE')
        ? 'bg-amber-500/10 border border-amber-500/30'
        : 'bg-red-500/10 border border-red-500/30'
    }`}>
      <h4 className="font-semibold mb-3">Bias Analysis Result</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-muted-foreground mb-1">Original Classification Gap</p>
          <p className="text-lg">
            Female: <span className="font-bold text-pink-400">{biasAudit.bias_analysis?.original_classification_gap?.female_care_pct}%</span> care
            {' | '}
            Male: <span className="font-bold text-blue-400">{biasAudit.bias_analysis?.original_classification_gap?.male_care_pct}%</span> care
          </p>
          <p className="text-sm font-mono">Gap: +{biasAudit.bias_analysis?.original_classification_gap?.gap}%</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">Blind Classification Gap</p>
          {biasAudit.bias_analysis?.blind_classification_gap?.female_care_pct !== null ? (
            <>
              <p className="text-lg">
                Female: <span className="font-bold text-pink-400">{biasAudit.bias_analysis?.blind_classification_gap?.female_care_pct}%</span>
                {' | '}
                Male: <span className="font-bold text-blue-400">{biasAudit.bias_analysis?.blind_classification_gap?.male_care_pct}%</span>
              </p>
              <p className="text-sm font-mono">Gap: +{biasAudit.bias_analysis?.blind_classification_gap?.gap}%</p>
            </>
          ) : (
            <p className="text-muted-foreground italic">Need more blind codings to calculate</p>
          )}
        </div>
      </div>
      <div className="flex items-start gap-2">
        {biasAudit.bias_analysis?.bias_conclusion?.includes('VALIDATED') ? (
          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
        ) : biasAudit.bias_analysis?.bias_conclusion?.includes('INCONCLUSIVE') ? (
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
        ) : (
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        )}
        <p className="font-medium">{biasAudit.bias_analysis?.bias_conclusion}</p>
      </div>
    </div>

    {/* Methodology */}
    <div className="text-sm text-muted-foreground">
      <p className="font-medium mb-2">Bias Mitigation Applied:</p>
      <ul className="list-disc list-inside space-y-1">
        {biasAudit.methodology?.bias_mitigation_applied?.map((item, i) => (
          <li key={`bias-${i}-${item.slice(0,20)}`}>{item}</li>
        ))}
      </ul>
    </div>
  </div>
);

export default ResearchModePanel;
